/**
 * round7/signup-ui (P4.5-B) — now also renders a portal-wide, dismissible-
 * per-session trial banner, on top of its original job (redirect to /login
 * when signed out).
 *
 * === round7/org-admin-panel-ui (P4.6-C): why this gained a `scope` prop
 * rather than a parallel OrgProtectedRoute component ===
 * P4.6-B deliberately built AdminProtectedRoute as a SEPARATE component
 * "sharing nothing with it" — correct there, because a platform admin is a
 * different identity on a different Cognito app client, authenticated through
 * a different authorizer, and the isolation between those two planes is a
 * real security boundary that a shared component would erode.
 *
 * NONE of that applies to an org admin. `orgAdmin` is a `custom:role` value
 * on the SAME app client, verified by the SAME portalAuthorizer, carried in
 * the SAME AuthContext session. Everything this component does — probe the
 * session, show the loading screen, bounce to /login when signed out — is
 * identical for both scoping levels, and it is precisely the part that must
 * never diverge between them. A second component would have to duplicate the
 * signed-out gate, which is the one piece of this file that is genuinely
 * security-relevant.
 *
 * What actually differs is two things, both small and both naturally a
 * branch: whether the trial banner's tenant-scoped fetch runs at all, and
 * which role the route admits. So this stays one component with one optional
 * prop, defaulting to `"tenant"` — every pre-P4.6-C call site in main.tsx is
 * unchanged, and the new /organization subtree passes `scope="org"`.
 *
 * A deliberate NON-goal: this is a UX router, not an authorization boundary.
 * It stops an org admin from staring at a page of failed requests; it is not
 * what stops them reading a tenant's leads. That is portalAuthorizer.ts plus
 * getPortalContext's explicit orgAdmin refusal, server-side, which hold
 * regardless of what this component renders.
 *
 * === Why here, architecturally ===
 * Every protected route already passes through this ONE component (see
 * main.tsx — /leads, /leads/:id, /rate-card, /billing each wrap their
 * element in <ProtectedRoute>), so it's the natural single injection point
 * for a banner that must appear on every authenticated page, rather than
 * duplicating fetch/render logic onto each page individually.
 *
 * This does mean ProtectedRoute now makes its OWN independent
 * GET /api/portal/tenant call, separate from whatever the wrapped page also
 * fetches for its own needs (BillingPage.tsx and SettingsPage.tsx both
 * already call getTenant() themselves, on their own mount effects) — a
 * real, deliberate redundant-fetch cost on those two routes, not an
 * oversight. Confirmed architecturally sound for THIS codebase specifically
 * (not asserted as a general best practice) because there is no shared
 * data-fetching/caching layer anywhere in this SPA to plug into instead: no
 * context wraps tenant data, no react-query, no cache of any kind — every
 * existing page that needs the tenant already fetches it fully
 * independently, on its own mount effect. Adding one more independent
 * fetch site here is consistent with that established (lack of) caching
 * architecture, not a new anti-pattern layered on top of a clean one.
 * Lifting tenant-fetching into AuthContext so every consumer could share a
 * single fetch would be a real architecture change touching already-tested
 * pages, well beyond "the banner + billing states" this round is scoped to
 * (see the task's own "no changes to authenticated features beyond..."
 * fence) — flagged here rather than done silently.
 *
 * `GET /api/portal/tenant` is a cheap, side-effect-free, already-
 * authenticated read; a failure here is swallowed (except 401, which still
 * signs out, matching every other call site's convention) rather than
 * shown, since this banner is a secondary convenience, never a gate — the
 * wrapped page's OWN tenant fetch (where it has one) is what surfaces a
 * real, visible load error through its own established UI.
 *
 * === Dismissal: sessionStorage, not localStorage ===
 * Per the task's own wording, "dismissible per session" means it should
 * reappear on a genuinely new browser session, not be silenced forever the
 * first time someone dismisses it — sessionStorage (cleared when the tab/
 * browser session ends) is what gives that, where localStorage would not.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import { getTenant, ApiError, type TenantConfig } from "../lib/api";
import { TenantShell } from "./shell/TenantShell";
import { OrgShell } from "./shell/OrgShell";
import { deriveBillingViewState, trialBannerMessage } from "../lib/billing";
import { isOrgAdmin, ORG_ADMIN_HOME, TENANT_HOME } from "../lib/authRole";
import styles from "./ProtectedRoute.module.css";

const TRIAL_BANNER_DISMISSED_KEY = "dockpro:trialBannerDismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(TRIAL_BANNER_DISMISSED_KEY) === "true";
  } catch {
    // Private-browsing / storage-disabled — fail open to "not dismissed"
    // (i.e. still eligible to show the banner) rather than throwing; this
    // banner is a convenience, never a page-blocking gate.
    return false;
  }
}

/**
 * Which scoping level a route requires. `"tenant"` is the default so every
 * pre-existing call site in main.tsx is byte-identical to before P4.6-C.
 */
export type RouteScope = "tenant" | "org";

export function ProtectedRoute(
  { children, scope = "tenant" }: { children: ReactNode; scope?: RouteScope },
) {
  const { user, isLoading, signOut } = useAuth();
  // round9/design-pass: the whole tenant is kept, not just the trial days —
  // the shell shows branding.builderName in the topbar, from this same read.
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  // THE ROLE BRANCH (P4.6-C). An org admin's token has no tenantId, so
  // `GET /api/portal/tenant` is not merely unhelpful for them — it is
  // guaranteed to fail (DockPro-Backend's getPortalContext THROWS on an
  // orgAdmin context, which API Gateway returns as a 502). The existing
  // catch below would swallow that silently, so the banner would simply
  // never appear; but it would still fire a doomed request on every
  // org-admin page view and log a 502 that looks like a real backend fault.
  // So the fetch is skipped outright rather than swallowed.
  const wantsTrialBanner = !!user && !isOrgAdmin(user);

  useEffect(() => {
    if (!user || !wantsTrialBanner) return;
    let cancelled = false;
    getTenant(user.idToken)
      .then((result) => {
        if (cancelled) return;
        setTenant(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        // Deliberately silent otherwise — see file header.
      });
    return () => { cancelled = true; };
  }, [user, wantsTrialBanner, signOut]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(TRIAL_BANNER_DISMISSED_KEY, "true");
    } catch {
      // Storage unavailable — `dismissed` state above still hides the
      // banner for the rest of this mount; it just won't survive a route
      // navigation (a freshly-mounted ProtectedRoute re-reads storage and
      // finds nothing). Acceptable for a convenience banner.
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  // SCOPE MISMATCH — send them to the section their role can actually use.
  // Both directions are handled here, in ONE place, so the two cannot drift:
  // an org admin on a tenant route, and an owner/rep on an org route (who
  // would otherwise get a 403 ORG_ADMIN_REQUIRED from every my-org call).
  // `replace` so the wrong-scope URL doesn't linger in history and send the
  // Back button straight back into a redirect.
  if (scope === "tenant" && isOrgAdmin(user)) {
    return <Navigate to={ORG_ADMIN_HOME} replace />;
  }
  if (scope === "org" && !isOrgAdmin(user)) {
    return <Navigate to={TENANT_HOME} replace />;
  }

  const viewState = tenant ? deriveBillingViewState(tenant) : null;
  const trialDaysRemaining = viewState?.kind === "trialing" ? viewState.daysRemaining : null;
  const showBanner = trialDaysRemaining !== null && !dismissed;

  const banner = showBanner ? (
    <div role="region" aria-label="Trial status" className={styles.trialBanner}>
      <span>{trialBannerMessage(trialDaysRemaining!)}</span>
      <button
        type="button"
        onClick={handleDismiss}
        className={styles.dismissButton}
        aria-label="Dismiss trial banner"
      >
        ×
      </button>
    </div>
  ) : null;

  // round9/design-pass (Direction A): the plane's chrome is rendered HERE,
  // once, around every protected page — the topbar with primary nav for a
  // builder, the dark banner with nav + Sign out for an org admin — so a
  // page's loading and error branches get the same header as its loaded
  // state, and no page draws its own.
  if (scope === "org") {
    return <OrgShell>{children}</OrgShell>;
  }

  return (
    <TenantShell businessName={tenant?.branding?.builderName ?? null} banner={banner}>
      {children}
    </TenantShell>
  );
}
